/* AuthCallback — handles the return from https://auth.emergentagent.com.
 *
 * Emergent Auth lands the browser on /dashboard#session_id=<sid> after Google
 * sign-in. This component:
 *   1. Reads session_id from window.location.hash (URL fragment — NOT query)
 *   2. POSTs it to our backend which exchanges it for our JWT cookies
 *   3. Clears the hash and navigates to /dashboard
 *
 * Uses useRef (not useState) for the processed flag to be idempotent under
 * React StrictMode's double-invocation of effects.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError('Ingen session_id hittades i URL:en 💔');
      return;
    }

    (async () => {
      try {
        await axios.post(
          `${API}/auth/emergent-session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        // Clean the hash so we don't reprocess on back-navigation
        window.history.replaceState(null, '', window.location.pathname);
        // Populate AuthContext with the newly issued cookie
        await refreshUser();
        navigate('/dashboard', { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || 'Okänt fel';
        setError(`Google-inloggning misslyckades: ${detail}`);
      }
    })();
  }, [navigate, refreshUser]);

  return (
    <div className="min-h-screen bg-[#FFFAF0] flex items-center justify-center p-4" data-testid="auth-callback">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <div className="text-5xl mb-3">💔</div>
            <p className="text-sm text-red-800 mb-4">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="neo-btn px-6 py-2 bg-white border-2 border-black font-bold"
              data-testid="auth-callback-back-to-login"
            >
              Tillbaka till login
            </button>
          </>
        ) : (
          <>
            <div className="text-5xl mb-3 animate-pulse">🐾</div>
            <p className="text-sm text-gray-700 italic">Loggar in med Google...</p>
          </>
        )}
      </div>
    </div>
  );
}
